import type { FC, ReactNode } from 'react';
import { CheckCircle2, Info, AlertTriangle, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils/cn';

export type AlertVariant = 'success' | 'info' | 'warning' | 'error';

type Props = {
  variant?: AlertVariant;
  title?: string;
  children: ReactNode;
  className?: string;
};

const Alert: FC<Props> = ({ variant = 'info', title, children, className }) => {
  const styles: Record<AlertVariant, { wrap: string; icon: ReactNode }> = {
    success: {
      wrap: 'border border-green-200 bg-green-50 text-green-900',
      icon: <CheckCircle2 className="h-5 w-5 text-green-600" />
    },
    info: {
      wrap: 'border border-blue-200 bg-blue-50 text-blue-900',
      icon: <Info className="h-5 w-5 text-blue-600" />
    },
    warning: {
      wrap: 'border border-yellow-200 bg-yellow-50 text-yellow-900',
      icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />
    },
    error: {
      wrap: 'border border-red-200 bg-red-50 text-red-900',
      icon: <XCircle className="h-5 w-5 text-red-600" />
    }
  };

  const s = styles[variant];

  return (
    <div className={cn('rounded-lg p-4', s.wrap, className)} role="alert">
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{s.icon}</div>
        <div className="min-w-0">
          {title ? <div className="font-semibold">{title}</div> : null}
          <div className="text-sm">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default Alert;
